import { ReactElement } from 'react';
import { FormHelperText, Grid, InputLabel, Stack, Typography } from '@mui/material';
import { Path, type FieldValues, type UseFormReturn, type UseFormRegisterReturn } from 'react-hook-form';
import { get } from 'lodash';
import { useThemeMode } from '../themes/themeModeProvider';
import { ThemeMode } from '../types/config';

type RenderProps<Name extends string> = UseFormRegisterReturn<Name> & {
	readonly id: Name;
};

type Props<Values extends FieldValues, Name extends Path<Values>> = {
	readonly form: UseFormReturn<Values>;
	readonly name: Name;
	readonly description?: string;
	readonly render: (props: RenderProps<Name>) => ReactElement;
	readonly label?: string;
	readonly hint?: string;
};

const FormField = <Values extends FieldValues, Name extends Path<Values>>({
	form,
	name,
	label,
	description,
	hint,
	render,
}: Props<Values, Name>): ReactElement => {
	const { mode } = useThemeMode();
	return (
		<Grid item xs={12}>
			<Stack spacing={1}>
				{label && (
					<InputLabel htmlFor={name} sx={{ fontWeight: 700 }}>
						{label}
					</InputLabel>
				)}
				{description && <FormHelperText>{description}</FormHelperText>}

				{render({
					...form.register(name),
					id: name,
				})}
				{get(form.formState.errors, name) == null ? (
					hint && <FormHelperText>{hint}</FormHelperText>
				) : (
					<FormHelperText error>
						<Typography variant={'body2'} color={mode === ThemeMode.DARK ? 'primary' : 'error'}>
							{get(form.formState.errors, name)?.message as string}
						</Typography>
					</FormHelperText>
				)}
			</Stack>
		</Grid>
	);
};

export { FormField };
